import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTable, useGlobalFilter, useFilters, usePagination } from "react-table";
import { Row, Col, Button } from "react-bootstrap";
import { GlobalFilter } from "../components/table/FilteringTable/GlobalFilter";
import "../components/table/FilteringTable/filtering.css";
import { ColumnFilter } from "../components/table/FilteringTable/ColumnFilter";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useDispatch } from "react-redux";
import { Fn_DeleteData, Fn_FillListData } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import { toast } from "react-toastify";

import useSession from "../utils/useSession";

export const PageList_EmployeeMaster = () => {
  const [gridData, setGridData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { firmId } = useSession();

  const LIST_API_URL = `${API_WEB_URLS.MASTER}/0/token/EmployeeDatabyFirm/Id/${firmId || 0}`;
  const DELETE_API_URL = `${API_WEB_URLS.MASTER}/0/token/EmployeeMaster`;

  /**
   * Loads employee master list.
   */
  const loadData = useCallback(() => {
    if (firmId) {
      Fn_FillListData(dispatch, setGridData, "gridData", LIST_API_URL).catch((error) => {
        console.error("Failed to load employee masters:", error);
      });
    } else {
      setGridData([]);
    }
  }, [dispatch, LIST_API_URL, firmId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Navigate to add screen.
   */
  const handleAdd = () => {
    navigate("/AddEmployee", { state: { Id: 0 } });
  };

  /**
   * Navigate to edit screen.
   */
  const handleEdit = (id) => {
    if (!id) return;
    navigate("/AddEmployee", { state: { Id: id } });
  };

  /**
   * Delete selected employee master.
   */
  const handleDelete = (id, name) => {
    if (!id) return;
    if (window.confirm(`Are you sure you want to delete this employee master${name ? ` (${name})` : ""}?`)) {
      Fn_DeleteData(dispatch, setGridData, Number(id), DELETE_API_URL, LIST_API_URL)
        .then(() => {
          toast.success("Employee deleted successfully");
          loadData();
        })
        .catch((error) => {
          console.error("Failed to delete employee master:", error);
        });
    }
  };

  const COLUMNS = [
    { Header: "Sr No",       accessor: "SerialNo",        Filter: ColumnFilter },
    { Header: "Employee Code", accessor: "CardNo",          Filter: ColumnFilter },
    { Header: "Name",        accessor: "Name",            Filter: ColumnFilter },
    { Header: "Father Name", accessor: "FatherName",      Filter: ColumnFilter },
    { Header: "Department",  accessor: "DepartmentName",  Filter: ColumnFilter },
    { Header: "Designation", accessor: "DesignationName", Filter: ColumnFilter },
    { Header: "Mobile No",   accessor: "MobileNo",        Filter: ColumnFilter },
    { Header: "Emp. Type",   accessor: "EmployeeType",    Filter: ColumnFilter },
    { Header: "Sex",         accessor: "Sex",             Filter: ColumnFilter },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <Button variant="primary" size="sm" className="me-2" onClick={() => handleEdit(row.original.Id)} title="Edit">
            <i className="fa fa-edit" />
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleDelete(row.original.Id, row.original.Name)} title="Delete">
            <i className="fa fa-trash" />
          </Button>
        </div>
      ),
    },
  ];

  const columns = useMemo(() => COLUMNS, []);
  const data    = useMemo(() => gridData, [gridData]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useFilters, useGlobalFilter, usePagination
  );

  const {
    getTableProps, getTableBodyProps, headerGroups, prepareRow,
    state, page, gotoPage, pageCount, pageOptions,
    nextPage, previousPage, canNextPage, canPreviousPage, setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  return (
    <Container fluid className="page-content">
      <Row className="mb-3 align-items-center">
        <Col md="6">
          <h4 className="page-title mb-0" style={{ fontFamily: "Poppins" }}>Employee Master</h4>
        </Col>
        <Col md="auto" className="ms-auto d-flex gap-2">
          <Button
            type="button"
            onClick={() => navigate("/EmployeeImportExport")}
            variant="outline-primary"
            size="sm"
            style={{ fontWeight: 600 }}
          >
            <i className="fas fa-file-excel me-1"></i>Import / Export
          </Button>
          <Button type="button" onClick={handleAdd} variant="success" size="sm">
            <i className="fas fa-plus me-1"></i>Add New
          </Button>
        </Col>
      </Row>
      <Row className="mb-2">
        <Col md="12">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </Col>
      </Row>
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table {...getTableProps()} className="table dataTable display">
              <thead>
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()}>
                    {hg.headers.map((col) => (
                      <th {...col.getHeaderProps()}>
                        {col.render("Header")}
                        {col.canFilter ? col.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="d-flex justify-content-between">
              <span>Page <strong>{pageIndex + 1} of {pageOptions.length}</strong></span>
              <span className="table-index">
                Go to page:{" "}
                <input
                  type="number"
                  className="ml-2"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => gotoPage(e.target.value ? Number(e.target.value) - 1 : 0)}
                />
              </span>
            </div>
            <div className="text-center">
              <div className="filter-pagination mt-3">
                <button className="previous-button" onClick={() => gotoPage(0)}           disabled={!canPreviousPage}>{"<<"}</button>
                <button className="previous-button" onClick={() => previousPage()}         disabled={!canPreviousPage}>Previous</button>
                <button className="next-button"     onClick={() => nextPage()}             disabled={!canNextPage}>Next</button>
                <button className="next-button"     onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{">>"}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default PageList_EmployeeMaster;
